import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import { canOfferAct } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import type {
  GeneralAgent,
  GeneralAgentListParams,
} from '@/lib/api/domains/general-agents';
import { generalAgentListQuery } from '../api/general-agent-queries';
import { generalAgentColumns } from './general-agent-columns';

export function GeneralAgentTable({
  params,
  onPageChange,
  onRowClick,
  onEdit,
}: {
  params: GeneralAgentListParams;
  onPageChange: (page: number) => void;
  onRowClick: (generalAgent: GeneralAgent) => void;
  onEdit?: (generalAgent: GeneralAgent) => void;
}) {
  const query = useQuery(generalAgentListQuery(params));
  const session = useAuthStore((s) => s.session);
  // The per-row edit is the F04 entity-edit act (Administrator-only), read
  // per-act from the server-sourced capabilities — a `tpabilling_user` (Viewer)
  // holds the read, not this act, so the control is withheld (E02.F04.R5,
  // AC-9311d02c). Courtesy only; the domain gate refuses regardless.
  const showEdit =
    Boolean(onEdit) && canOfferAct(session, 'tpabilling.enrollment.edit');

  return (
    <DataTable<GeneralAgent>
      columns={generalAgentColumns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.id}
      tableId="general-agents"
      actions={
        showEdit
          ? (generalAgent) => (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${generalAgent.name}`}
                onClick={() => onEdit?.(generalAgent)}
              >
                <Pencil className="size-4" />
              </Button>
            )
          : undefined
      }
      isLoading={query.isLoading}
      isError={query.isError}
      errorMessage="Failed to load general agents."
      emptyTitle="No general agents found"
      page={query.data?.page ?? params.page ?? 1}
      pageSize={query.data?.pageSize ?? params.pageSize ?? 25}
      total={query.data?.total ?? 0}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
    />
  );
}
